export interface NavItem {
  label: string;
  route: string;
  icon: string;
  /** Empty / omitted = visible to all authenticated users */
  roles?: string[];
  keywords?: string[];
}

export interface NavSection {
  id: string;
  label: string;
  icon: string;
  items: NavItem[];
  /** When true, section is a single top-level link (no accordion children UI needed for one item) */
  flat?: boolean;
  roles?: string[];
}

/** @deprecated Use NavSection */
export type NavGroup = { label: string; items: NavItem[] };

const ALL_ADMIN = ['super_admin', 'tenant_admin'];
const HR_ROLES = [...ALL_ADMIN, 'hr'];
const FINANCE_ROLES = [...ALL_ADMIN, 'finance', 'hr'];
const PM_ROLES = [...ALL_ADMIN, 'project_manager', 'team_lead', 'aso'];
const WORK_ROLES = [...PM_ROLES, 'employee', 'developer', 'designer', 'qa'];

/**
 * Single-sidebar accordion sections.
 * Routes are unchanged — only presentation grouping.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'dashboard',
    flat: true,
    items: [
      { label: 'Dashboard', route: '/dashboard', icon: 'dashboard', keywords: ['home', 'overview'] }
    ]
  },
  {
    id: 'hr',
    label: 'HR Management',
    icon: 'badge',
    items: [
      { label: 'Employees', route: '/employees', icon: 'groups', roles: HR_ROLES },
      { label: 'HR Hub', route: '/hr', icon: 'corporate_fare', roles: HR_ROLES },
      { label: 'Attendance', route: '/attendance', icon: 'event_available', roles: [...HR_ROLES, 'employee'] },
      { label: 'Leave', route: '/leave', icon: 'event_busy', roles: [...HR_ROLES, 'employee', 'team_lead', 'project_manager'] },
      { label: 'Recruitment', route: '/recruitment', icon: 'person_search', roles: HR_ROLES },
      { label: 'Performance', route: '/performance', icon: 'monitoring', roles: [...HR_ROLES, 'team_lead', 'project_manager'] },
      { label: 'Departments', route: '/departments', icon: 'apartment', roles: HR_ROLES },
      { label: 'Assets', route: '/assets', icon: 'devices', roles: [...FINANCE_ROLES, 'hr'] }
    ]
  },
  {
    id: 'pm',
    label: 'Project Management',
    icon: 'view_kanban',
    items: [
      { label: 'My Work', route: '/my-work', icon: 'assignment_ind', roles: WORK_ROLES, keywords: ['tasks', 'mine'] },
      { label: 'Accounts', route: '/accounts', icon: 'business_center', roles: [...PM_ROLES, ...ALL_ADMIN, 'hr'], keywords: ['portfolio', 'account'] },
      { label: 'Projects', route: '/projects', icon: 'folder_special', roles: [...PM_ROLES, ...ALL_ADMIN, 'hr'] },
      { label: 'Backlog', route: '/issues', icon: 'inventory_2', roles: WORK_ROLES, keywords: ['issues', 'tickets'] },
      { label: 'Tasks', route: '/tasks', icon: 'add_task', roles: [...PM_ROLES, ...ALL_ADMIN, 'hr'], keywords: ['project tasks', 'assign', 'team lead'] },
      { label: 'Epics', route: '/epics', icon: 'flag', roles: WORK_ROLES, keywords: ['epic', 'progress'] },
      { label: 'Team Lead', route: '/team-lead', icon: 'supervisor_account', roles: [...ALL_ADMIN, 'team_lead', 'project_manager', 'aso'] },
      { label: 'Scrum', route: '/scrum', icon: 'view_timeline', roles: PM_ROLES },
      { label: 'Kanban', route: '/kanban', icon: 'view_kanban', roles: PM_ROLES },
      { label: 'Workflows', route: '/workflows', icon: 'account_tree', roles: PM_ROLES },
      { label: 'Approvals', route: '/pm-approvals', icon: 'approval', roles: PM_ROLES },
      { label: 'Automation', route: '/automation', icon: 'smart_toy', roles: PM_ROLES },
      { label: 'PM AI', route: '/pm-ai', icon: 'auto_awesome', roles: PM_ROLES, keywords: ['ai', 'suggest', 'standup'] },
      { label: 'Advanced PM', route: '/pm-advanced', icon: 'tune', roles: PM_ROLES, keywords: ['portfolio', 'roadmap', 'timeline'] },
      { label: 'Bugs', route: '/bugs', icon: 'bug_report', roles: [...PM_ROLES, 'qa'] },
      { label: 'QA', route: '/qa', icon: 'verified', roles: [...ALL_ADMIN, 'qa', 'project_manager', 'team_lead'] },
      { label: 'Time Tracking', route: '/time-tracking', icon: 'schedule', roles: WORK_ROLES }
    ]
  },
  {
    id: 'finance',
    label: 'Finance',
    icon: 'account_balance',
    items: [
      { label: 'Expenses', route: '/finance', icon: 'receipt_long', roles: FINANCE_ROLES, keywords: ['finance', 'budgets'] },
      { label: 'Payroll', route: '/payroll', icon: 'payments', roles: FINANCE_ROLES },
      { label: 'Budgets', route: '/finance', icon: 'pie_chart', roles: FINANCE_ROLES }
    ]
  },
  {
    id: 'communication',
    label: 'Communication',
    icon: 'forum',
    items: [
      { label: 'Chat', route: '/chat', icon: 'chat' },
      { label: 'Meetings', route: '/meetings', icon: 'video_call' },
      { label: 'Calendar', route: '/calendar', icon: 'calendar_month' },
      { label: 'Files', route: '/files', icon: 'folder' },
      { label: 'Knowledge', route: '/knowledge', icon: 'menu_book' }
    ]
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'analytics',
    items: [
      { label: 'HR Reports', route: '/reports', icon: 'description', roles: [...HR_ROLES, ...FINANCE_ROLES, 'project_manager'] },
      { label: 'Project Reports', route: '/pm-reports', icon: 'insights', roles: PM_ROLES },
      { label: 'Finance Reports', route: '/finance', icon: 'bar_chart', roles: FINANCE_ROLES }
    ]
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: 'admin_panel_settings',
    items: [
      { label: 'Organizations', route: '/organizations', icon: 'domain', roles: ALL_ADMIN },
      { label: 'Users', route: '/users', icon: 'manage_accounts', roles: ALL_ADMIN },
      { label: 'Admin', route: '/admin', icon: 'shield_person', roles: ALL_ADMIN, keywords: ['roles', 'permissions', 'audit'] },
      { label: 'Integrations', route: '/integrations', icon: 'extension', roles: ALL_ADMIN },
      { label: 'AI Assistant', route: '/ai-assistant', icon: 'smart_toy' },
      { label: 'Notifications', route: '/notifications', icon: 'notifications' },
      { label: 'Settings', route: '/settings', icon: 'settings' }
    ]
  }
];

export const NAV_GROUPS: NavGroup[] = NAV_SECTIONS.map((s) => ({
  label: s.label,
  items: s.items
}));

export function itemVisible(item: NavItem, userRoles: string[]): boolean {
  const normalized = userRoles.map((r) => r.toLowerCase());
  if (normalized.includes('super_admin') || !item.roles?.length) return true;
  return item.roles.some((role) => normalized.includes(role.toLowerCase()));
}

export function filterNavSections(sections: NavSection[], userRoles: string[]): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => itemVisible(item, userRoles))
    }))
    .filter((section) => section.items.length > 0);
}

export function filterNavByRoles(groups: NavGroup[], userRoles: string[]): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => itemVisible(item, userRoles))
    }))
    .filter((group) => group.items.length > 0);
}

export function findNavItem(route: string): NavItem | undefined {
  const path = route.split('?')[0];
  for (const section of NAV_SECTIONS) {
    const match = section.items.find(
      (item) => item.route === path || path.startsWith(item.route + '/')
    );
    if (match) return match;
  }
  return undefined;
}

export function findNavSectionForRoute(route: string): NavSection | undefined {
  const path = route.split('?')[0];
  return NAV_SECTIONS.find((section) =>
    section.items.some((item) => item.route === path || path.startsWith(item.route + '/'))
  );
}

export function findNavTitle(route: string): string {
  return findNavItem(route)?.label ?? 'Dashboard';
}

export function searchNavItems(query: string, userRoles: string[]): NavItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const items = NAV_SECTIONS.flatMap((s) => s.items).filter((item) => itemVisible(item, userRoles));
  const seen = new Set<string>();
  return items
    .filter((item) => {
      if (seen.has(item.route)) return false;
      const hay = [item.label, item.route, ...(item.keywords ?? [])].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
      seen.add(item.route);
      return true;
    })
    .slice(0, 12);
}

export const QUICK_CREATE_ACTIONS: { label: string; route: string; icon: string; roles?: string[] }[] = [
  { label: 'Employee', route: '/employees/new', icon: 'person_add', roles: HR_ROLES },
  { label: 'Leave request', route: '/leave', icon: 'event_busy', roles: [...HR_ROLES, 'employee', 'team_lead', 'project_manager'] },
  { label: 'Issue', route: '/issues', icon: 'confirmation_number', roles: WORK_ROLES },
  { label: 'Task', route: '/tasks', icon: 'add_task', roles: [...PM_ROLES, ...ALL_ADMIN, 'hr'] },
  { label: 'Project', route: '/projects', icon: 'create_new_folder', roles: [...PM_ROLES, ...ALL_ADMIN, 'hr'] },
  { label: 'Meeting', route: '/meetings', icon: 'video_call' }
];
